import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SharedFooter } from "@/components/common/SharedFooter";
import { useAuth } from "@/contexts/AuthContext";

export default function TermeniSiConditii() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isLoggedIn = !!user;

  useEffect(() => {
    document.title = "Termeni și Condiții — NIS2PASS";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Termenii și condițiile de utilizare a platformei NIS2PASS."
      );
    } else {
      const newMeta = document.createElement("meta");
      newMeta.name = "description";
      newMeta.content =
        "Termenii și condițiile de utilizare a platformei NIS2PASS.";
      document.head.appendChild(newMeta);
    }
  }, []);

  useEffect(() => {
    const id = "lp-google-fonts";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Space+Mono:wght@400;700&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="lp-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Space+Mono:wght@400;700&display=swap');

        :root {
          --c-navy: #0F172A;
          --c-navy-dark: #1E293B;
          --c-navy-darkest: #0B1220;
          --c-blue: #2563EB;
          --c-blue-dark: #1D4ED8;
          --c-blue-light: #60A5FA;
          --c-orange: #FF8400;
          --c-orange-hover: #FF9520;
          --c-bg-light: #F8FAFC;
          --c-bg-page: #F7FAFF;
          --c-border-light: #E2E8F0;
          --c-border-mid: #CBD5E1;
          --c-text-body: #475569;
          --c-text-muted: #64748B;
          --c-text-dim: #94A3B8;
          --c-text-faint: #CBD5E1;
          --c-white: #FFF;
          --c-black: #111;
        }

        .lp-page { background: var(--c-bg-light); color: var(--c-navy); font-family: 'Sora', system-ui, sans-serif; min-height: 100vh; display: flex; flex-direction: column; }
        .lp-page * { box-sizing: border-box; margin: 0; padding: 0; }
        .lp-page a { text-decoration: none; color: inherit; }

        /* NAV */
        .lp-nav { width: 100%; background: var(--c-navy); padding: 28px 0; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .lp-nav-inner { max-width: 1200px; width: 100%; padding: 0 24px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; }
        .lp-nav-logo { display: flex; align-items: center; gap: 8px; cursor: pointer; text-decoration: none; }
        .lp-nav-logo-mark { width: 14px; height: 14px; background: #22C55E; border-radius: 2px; flex-shrink: 0; }
        .lp-nav-brand { font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 700; color: var(--c-white); }
        .lp-nav-links { display: flex; align-items: center; gap: 24px; }
        .lp-nav-link { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-faint); cursor: pointer; transition: color 150ms; background: none; border: none; padding: 0; }
        .lp-nav-link:hover { color: var(--c-white); }
        .lp-nav-link.active { color: var(--c-blue-light); }
        .lp-nav-btns { display: flex; align-items: center; gap: 12px; }
        .lp-nav-ghost { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-faint); background: transparent; border: 1px solid #334155; padding: 10px 18px; border-radius: 999px; cursor: pointer; transition: all 200ms; }
        .lp-nav-ghost:hover { border-color: var(--c-blue-light); color: var(--c-white); background: rgba(96,165,250,0.08); }
        .lp-nav-cta { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-black); background: var(--c-orange); border: none; padding: 10px 18px; border-radius: 999px; cursor: pointer; font-weight: 500; transition: all 200ms; }
        .lp-nav-cta:hover { background: var(--c-orange-hover); transform: translateY(-1px); box-shadow: 0 4px 12px rgba(255,132,0,0.3); }

        /* MAIN CONTENT */
        .lp-main { flex: 1; max-width: 1120px; width: 100%; margin: 0 auto; padding: 80px 24px; }

        /* HEADER */
        .lp-label { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-blue); letter-spacing: 1.4px; text-transform: uppercase; margin-bottom: 12px; }
        .lp-title { font-family: 'Sora', sans-serif; font-size: 38px; font-weight: 700; color: var(--c-navy); letter-spacing: -1.2px; line-height: 1.08; margin-bottom: 32px; }

        /* DOCUMENT CARD */
        .lp-card { background: var(--c-white); border: 1px solid var(--c-border-light); border-radius: 8px; padding: 32px 36px; position: relative; }

        .lp-doc-title { font-family: 'Sora', sans-serif; font-size: 24px; font-weight: 700; color: var(--c-navy); margin-bottom: 8px; }
        .lp-doc-date { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-muted); margin-bottom: 4px; }
        .lp-doc-company { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-muted); margin-bottom: 16px; }
        .lp-doc-divider { border: none; height: 1px; background: var(--c-border-light); margin: 16px 0 24px; }
        .lp-doc-heading { font-family: 'Sora', sans-serif; font-size: 16px; font-weight: 700; color: var(--c-navy); margin: 24px 0 8px; }
        .lp-doc-body { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-body); line-height: 1.45; margin-bottom: 8px; }
        .lp-doc-body strong { color: var(--c-navy); font-weight: 700; }
        .lp-doc-list { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-body); line-height: 1.45; margin: 4px 0 12px 20px; list-style: disc; }
        .lp-doc-list li { margin-bottom: 4px; }
        .lp-doc-list li strong { color: var(--c-navy); font-weight: 700; }

        /* RESPONSIVE */
        @media (max-width: 768px) {
          .lp-main { padding: 48px 24px; }
          .lp-title { font-size: 28px; letter-spacing: -0.8px; margin-bottom: 24px; }
          .lp-card { padding: 24px 20px; }
          .lp-doc-title { font-size: 20px; }
          .lp-nav-links, .lp-nav-btns { display: none; }
        }
      `}</style>

      {/* NAV */}
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <a href="/" className="lp-nav-logo">
            <span className="lp-nav-logo-mark" />
            <span className="lp-nav-brand">NIS2PASS</span>
          </a>
          <div className="lp-nav-links">
            <button
              type="button"
              className="lp-nav-link"
              onClick={() => navigate("/")}
            >
              Platforma
            </button>
            <button
              type="button"
              className="lp-nav-link active"
              onClick={() => navigate("/termeni-si-conditii")}
            >
              Legal
            </button>
            <button
              type="button"
              className="lp-nav-link"
              onClick={() => navigate("/contact")}
            >
              Contact
            </button>
          </div>
          <div className="lp-nav-btns">
            {isLoggedIn ? (
              <>
                <button
                  type="button"
                  className="lp-nav-cta"
                  onClick={() => navigate("/dashboard")}
                >
                  Dashboard
                </button>
                <button
                  type="button"
                  className="lp-nav-ghost"
                  onClick={() => navigate("/logout")}
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="lp-nav-ghost"
                  onClick={() => navigate("/auth?mode=signin")}
                >
                  Intra in cont
                </button>
                <button
                  type="button"
                  className="lp-nav-cta"
                  onClick={() => navigate("/auth")}
                >
                  Creeaza cont gratuit
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* MAIN */}
      <main className="lp-main">
        <p className="lp-label">DOCUMENTE LEGALE NIS2PASS</p>
        <h1 className="lp-title">Termeni și Condiții</h1>

        <div className="lp-card">
          <h2 className="lp-doc-title">Termeni și Condiții — Versiunea 3.0</h2>
          <p className="lp-doc-date">22 Septembrie 2025</p>
          <p className="lp-doc-company">
            Compliance Made Easy SRL · CUI: 48993456 · J2023019988400
          </p>
          <hr className="lp-doc-divider" />

          <h3 className="lp-doc-heading">1. Definiții</h3>
          <p className="lp-doc-body">
            <strong>Platforma/Serviciul:</strong> Aplicația software NIS2PASS,
            accesibilă online, care permite Utilizatorilor să introducă Codul
            Unic de Identificare (CUI) al unei entități pentru a prelua și
            vizualiza date publice agregate, cu scopul de a asista în procesul
            de autoevaluare a conformității cu Directiva NIS2.
          </p>
          <p className="lp-doc-body">
            <strong>Utilizator:</strong> Persoana fizică ce creează un cont și
            utilizează Serviciul în scopuri profesionale, fie în nume propriu,
            fie ca reprezentant desemnat al uneia sau mai multor entități
            juridice. Serviciul este destinat exclusiv utilizării în scopuri
            profesionale și de afaceri, nefiind conceput pentru a fi utilizat în
            calitate de consumator.
          </p>
          <p className="lp-doc-body">
            <strong>Date Publice:</strong> Informații despre entități juridice,
            preluate de la operatorii noștri care agregă date din surse publice
            oficiale (ex: data.gov.ro, ONRC, Ministerul Finanțelor).
          </p>
          <p className="lp-doc-body">
            <strong>Cont de Utilizator:</strong> Contul securizat creat de
            Utilizator pentru a accesa Platforma.
          </p>

          <h3 className="lp-doc-heading">2. Acceptarea Termenilor</h3>
          <p className="lp-doc-body">
            Utilizarea Platformei, inclusiv crearea unui cont, constituie
            acceptarea integrală și necondiționată a acestor Termeni. Dacă nu
            sunteți de acord cu acești Termeni, nu trebuie să utilizați
            Serviciul.
          </p>

          <h3 className="lp-doc-heading">3. Descrierea Serviciului</h3>
          <p className="lp-doc-body">
            Platforma NIS2PASS este un instrument software de asistență,
            conceput pentru a simplifica procesul de colectare a informațiilor
            necesare autoevaluării în conformitate cu cerințele DNSC pentru
            Directiva NIS2. Serviciul preia CUI-ul unei entități, interoghează
            baze de date publice prin intermediul unor operatori terți și
            prezintă Utilizatorului informații agregate relevante (ex: date
            financiare, coduri CAEN). Serviciul nu oferă consultanță juridică
            sau de conformitate. Este responsabilitatea exclusivă a
            Utilizatorului să verifice acuratețea și completitudinea
            informațiilor agregate de Platformă și să le coroboreze cu sursele
            oficiale. Rezultatele generate sunt de natură informativă, similar
            modului în care chiar instrumentul oficial al DNSC menționează că
            stabilirea încadrării este una preliminară.
          </p>

          <h3 className="lp-doc-heading">4. Contul de Utilizator</h3>
          <p className="lp-doc-body">
            Utilizatorul este singurul responsabil pentru menținerea
            confidențialității credențialelor contului său (nume de utilizator
            și parolă) și pentru toate activitățile care au loc sub contul său.
            Utilizatorul se obligă să notifice imediat Compania cu privire la
            orice utilizare neautorizată a contului său.
          </p>

          <h3 className="lp-doc-heading">5. Utilizare Permisă și Obligații</h3>
          <p className="lp-doc-body">
            Compania acordă Utilizatorului o licență limitată, neexclusivă,
            revocabilă și netransferabilă de a accesa și utiliza Platforma în
            scopurile interne ale afacerii sale, exclusiv pentru asistență în
            evaluarea conformității cu NIS2.
          </p>

          <h3 className="lp-doc-heading">6. Activități Interzise</h3>
          <p className="lp-doc-body">
            Utilizatorul se obligă să nu desfășoare și să nu permită niciunui
            terț să desfășoare următoarele activități:
          </p>
          <ul className="lp-doc-list">
            <li>
              Revânzarea, sublicențierea sau distribuirea datelor obținute prin
              Platformă.
            </li>
            <li>
              Utilizarea Platformei pentru a dezvolta un produs sau serviciu
              concurent.
            </li>
            <li>
              Utilizarea datelor de pe Platformă pentru a antrena modele de
              inteligență artificială (IA).
            </li>
            <li>
              Inginerie inversă (reverse engineering), decompilarea sau
              încercarea de a extrage codul sursă al Platformei.
            </li>
            <li>
              Utilizarea oricăror mijloace automate (roboți, scripturi,
              scrapers) pentru a accesa sau extrage date în masă de pe
              Platformă.
            </li>
            <li>
              Încercarea de a eluda măsurile de securitate ale Platformei sau de
              a accesa date pentru care nu are autorizare.
            </li>
            <li>
              Utilizarea Platformei într-un mod care ar putea deteriora,
              dezactiva sau supraîncărca infrastructura Companiei.
            </li>
          </ul>

          <h3 className="lp-doc-heading">7. Proprietate Intelectuală</h3>
          <p className="lp-doc-body">
            Platforma, inclusiv software-ul, designul, bazele de date, textele,
            grafica și orice alt material, sunt proprietatea exclusivă a
            Compliance Made Easy SRL și sunt protejate de legile privind
            drepturile de autor și proprietatea intelectuală. Acești Termeni nu
            acordă Utilizatorului niciun drept de proprietate asupra
            Serviciului.
          </p>

          <h3 className="lp-doc-heading">8. Confidențialitate</h3>
          <p className="lp-doc-body">
            Prelucrarea datelor cu caracter personal de către Companie este
            guvernată de Politica de Confidențialitate, care face parte
            integrantă din acești Termeni.
          </p>

          <h3 className="lp-doc-heading">9. Exonerare de Garanții</h3>
          <p className="lp-doc-body">
            Serviciul este furnizat &quot;așa cum este&quot; și &quot;așa cum
            este disponibil&quot;, fără nicio garanție de niciun fel, explicită
            sau implicită. Compania nu garantează că datele publice preluate
            sunt corecte, complete sau actuale, deoarece acestea provin din
            surse externe asupra cărora nu avem control. Utilizatorul își asumă
            întreaga responsabilitate pentru verificarea informațiilor și pentru
            deciziile luate pe baza acestora.
          </p>

          <h3 className="lp-doc-heading">10. Limitarea Răspunderii</h3>
          <p className="lp-doc-body">
            În măsura maximă permisă de lege, răspunderea totală a companiei,
            indiferent de cauză, nu va depăși suma totală plătită de utilizator
            pentru serviciu în cele 3 (trei) luni anterioare evenimentului care
            a generat pretenția. Compania nu va fi responsabilă pentru nicio
            daună indirectă, specială sau consecutivă (inclusiv pierderi de
            profit sau de date).
          </p>

          <h3 className="lp-doc-heading">11. Durata și Încetarea</h3>
          <p className="lp-doc-body">
            Acești Termeni intră în vigoare la crearea contului și rămân
            valabili pe durata utilizării Serviciului. Compania poate suspenda
            sau înceta accesul Utilizatorului la Serviciu în cazul încălcării
            acestor Termeni. Utilizatorul poate solicita ștergerea contului în
            orice moment, prin contactarea noastră la contact@nis2pass.ro.
          </p>

          <h3 className="lp-doc-heading">12. Lege Aplicabilă și Jurisdicție</h3>
          <p className="lp-doc-body">
            Acești Termeni sunt guvernați de legislația din România. Orice
            litigiu va fi soluționat de instanțele judecătorești competente din
            București, România.
          </p>

          <h3 className="lp-doc-heading">13. Modificări ale Termenilor</h3>
          <p className="lp-doc-body">
            Compania își rezervă dreptul de a modifica acești Termeni în orice
            moment. Modificările vor fi publicate pe Platformă, iar utilizarea
            continuă a Serviciului după publicare constituie acceptarea noilor
            Termeni.
          </p>
        </div>
      </main>

      {/* FOOTER */}
      <SharedFooter />
    </div>
  );
}
